import { axiosClient } from '@/shared';
import {
  DtoProjectType,
  DtoOutputLength,
  DtoAspectRatio,
  DtoResolution,
  DtoProjectStatus,
  DtoLanguage,
  DtoProviderType,
} from '@/shared/constants';
import { ApiResponse, ErrorResponse } from '@/shared/types';
import { resolveCurrentLocale } from './resolve-current-locale';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ContentInfoSchema } from '../content/content-info-schema';
import { getResolutionFromDimensions } from '@/features/remotion-editor/editor/rendering/scale-items-for-export';
import {
  ContentSourceRefSchema,
  type ContentSourceRefType,
} from './content-source-ref-schema';

// Re-export for backward compatibility
export { ContentInfoSchema } from '../content/content-info-schema';
export type { ContentInfoType } from '../content/content-info-schema';

// ===================================
// v2.0 Project Content Input Types
// ===================================

export { ContentSourceRefSchema };
export type { ContentSourceRefType };

// LOCAL_FILE: 로컬 파일 업로드 (presigned URL 발급)
export const ProjectContentInputLocalFileSchema = z.object({
  type: z.literal('LOCAL_FILE'),
  name: z.string(),
  /** FileExtension 정수 코드 (0: MP4, 1: MOV, 10: MP3 등) */
  extension: z.number(),
  size: z.number(),
  /** Content 레벨 (0: ORIGIN, 10: DERIVED, 20: SOURCE) */
  contentLevel: z.number().optional(),
  /** 타입별 Content 정보 (VideoContent, AudioContent, ImageContent, FontContent) */
  content: ContentInfoSchema,
  /** Space ID (Asset Upload 용) */
  spaceId: z.number().optional(),
  /** Content Provider 타입 (LOCAL, YOUTUBE 등) */
  providerType: z.nativeEnum(DtoProviderType).optional(),
  /** Provider URL (YouTube URL 등) */
  providerUrl: z.string().optional(),
  /** 원본 파일 참조. 절대경로 대신 rootId + relativePath 만 전달 */
  sourceRef: ContentSourceRefSchema.optional(),
});

export type ProjectContentInputLocalFileType = z.infer<
  typeof ProjectContentInputLocalFileSchema
>;

// EXISTING_CONTENT: 기존 Content 선택
export const ProjectContentInputExistingSchema = z.object({
  type: z.literal('EXISTING_CONTENT'),
  contentId: z.number(),
  inPoint: z.number().optional(),
  outPoint: z.number().optional(),
});

export type ProjectContentInputExistingType = z.infer<
  typeof ProjectContentInputExistingSchema
>;

// URL_CONTENT: URL 기반 Content (YouTube 등)
export const ProjectContentInputUrlSchema = z.object({
  type: z.literal('URL_CONTENT'),
  url: z.string(),
  title: z.string(),
  thumbnailUrl: z.string(),
  duration: z.number(),
  /** 트림 시작점 (밀리초) - 백엔드 지원 필요 */
  inPoint: z.number().optional(),
  /** 트림 끝점 (밀리초) - 백엔드 지원 필요 */
  outPoint: z.number().optional(),
});

export type ProjectContentInputUrlType = z.infer<
  typeof ProjectContentInputUrlSchema
>;

// Union type
export const ProjectContentInputSchema = z.discriminatedUnion('type', [
  ProjectContentInputLocalFileSchema,
  ProjectContentInputExistingSchema,
  ProjectContentInputUrlSchema,
]);

export type ProjectContentInputType = z.infer<typeof ProjectContentInputSchema>;

// ===================================
// v2.0 Request Schema
// ===================================

/**
 * 분석 필터 옵션 (voice 카테고리 테넌트 전용 — GS 등).
 * 모든 필드 생략 = BE/ai-api 기본 동작 (전 조건 제외 + 쇼핑호스트 전체 자동 감지).
 */
export const AnalysisOptionsSchema = z.object({
  commerceAnalysisEnabled: z.boolean().optional(),
  /** 우선 감지할 쇼핑호스트 code 목록. 생략 = 전체 자동 감지, [] = 우선 감지 OFF */
  voiceTargets: z.array(z.number()).optional(),
  /** 정확한 가격 직접 언급 구간 제외 (생략 = true) */
  excludeExactPrice: z.boolean().optional(),
  exactPriceFilterMode: z.enum(['off', 'penalty', 'exclude']).optional(),
  /** 시청자 댓글/채팅 반응 구간 제외 (생략 = true) */
  excludeCommentReaction: z.boolean().optional(),
  commentReactionFilterMode: z.enum(['off', 'penalty', 'exclude']).optional(),
  /** 방송 운영 멘트(매진/재고/주문) 구간 제외 (생략 = true, FE 미노출) */
  excludeMetaDiscussion: z.boolean().optional(),
  /** 사은품 설명 구간 제외 (생략 = true, FE 미노출) */
  excludeFreebie: z.boolean().optional(),
  /** 전면/스틸컷 정적 화면 감점 (vision 경로 전용) */
  excludeStaticScreens: z.boolean().optional(),
  staticScreenFilterMode: z.enum(['off', 'penalty', 'exclude']).optional(),
  /** 사용자가 직접 입력한 제외 키워드 목록 */
  excludeKeywords: z.array(z.string()).optional(),
});

export type AnalysisOptionsType = z.infer<typeof AnalysisOptionsSchema>;

export const TargetLengthRangeSecSchema = z.object({
  minSec: z.number(),
  maxSec: z.number(),
});

export type TargetLengthRangeSecType = z.infer<
  typeof TargetLengthRangeSecSchema
>;

/**
 * LTS 미드폼 생성 계약 (BE LtsMidformProfileDto 와 매칭).
 * target 180~1800초, tolerance 0~810초, outputCount 는 초기 릴리스 1 고정.
 */
export const LtsMidformProfileSchema = z.object({
  outputMode: z.literal('MIDFORM'),
  targetDurationSec: z.number(),
  durationToleranceSec: z.number(),
  outputCount: z.literal(1),
});

export type LtsMidformProfileType = z.infer<typeof LtsMidformProfileSchema>;

/**
 * LTS 자막 번역 요청 (BE CaptionTranslationRequestDto 와 매칭).
 * 필드 존재 = 번역 ON. 정책이 꺼진 space 는 BE 가 조용히 drop 하고, 미지원 언어는 400 이다.
 * 타겟 언어 어휘는 shared/lib/captions/caption-translation-languages.ts 와 동기.
 */
export const CaptionTranslationSchema = z.object({
  targetLanguage: z.enum(['en', 'ko']),
});

export type CaptionTranslationType = z.infer<typeof CaptionTranslationSchema>;

export const ProjectCreateRequestV2Schema = z.object({
  type: z.nativeEnum(DtoProjectType),
  name: z.string().optional(),
  spaceId: z.number().optional(),
  contents: z.array(ProjectContentInputSchema).optional(),
  language: z.nativeEnum(DtoLanguage).optional(),
  accountBrandTemplateId: z.number().optional(),
  aspectRatio: z.nativeEnum(DtoAspectRatio).optional(),
  voice: z.string().optional(),
  speedFactor: z.number().optional(),
  pitch: z.number().optional(),
  emotion: z.string().optional(),
  /** TTS 스타일 지시 (Gemini 전용, 다른 벤더는 무시). */
  style: z.string().optional(),
  includeTtsOnGenerate: z.boolean().optional(),
  // 콘텐츠 프리셋
  contentPreset: z.string().optional(),
  forceBetaNoReferenceFallback: z.boolean().optional(),
  contentPurpose: z.string().optional(),
  contentPurposeDetail: z.string().optional(),
  contentConclusionGuidance: z.string().optional(),
  contentTargetAge: z.string().optional(),
  contentTargetGender: z.string().optional(),
  contentScriptTone: z.string().optional(),
  contentSpeechStyle: z.string().optional(),
  // 추가 입력 필드
  productName: z.string().optional(),
  mandatoryContent: z.string().optional(),
  benchmarking: z.string().optional(),
  // 대본
  intro: z.string().optional(),
  body: z.string().optional(),
  outro: z.string().optional(),
  scriptPrompt: z.string().optional(),
  // Index Level: 10=STT_ONLY_LITE, 20=STT_LITE, 30=LTS (default)
  indexLevel: z.number().optional().default(30),
  // 포인트 검증
  checksum: z.number().optional(),
  broadcastVaultSourceIds: z.array(z.number()).optional(),
  // 분석 필터 옵션 (voice 카테고리 테넌트 전용). 생략 = 기본 동작
  analysisOptions: AnalysisOptionsSchema.optional(),
  /** LTS 자동 목표 길이. true(기본)면 ai-api 가 영상 길이 분포로 target 자동 도출. 생략=BE 기본 ON. */
  autoTargetLength: z.boolean().optional(),
  targetLengthRangeSec: TargetLengthRangeSecSchema.optional(),
  /**
   * LTS 미드폼(3~30분) 생성 계약 — indexLevel 50(미드폼 분석)과 상호 필수 (BE 검증).
   * autoTargetLength/targetLengthRangeSec 와 조합 금지. 오퍼레이터 베타.
   */
  ltsMidformProfile: LtsMidformProfileSchema.optional(),
  /** LTS 자막 번역. 생략 = 번역 없음(기존 동작). */
  captionTranslation: CaptionTranslationSchema.optional(),
  skipGsrSplitting: z.boolean().optional(),
  /**
   * 생성 시점 UI locale. BE 가 결과 화면 첫 USER_CHAT 문구를 이 값으로 고른다.
   * 콘텐츠 언어(`language`)와 별개 축이며, 호출부가 넘기지 않으면
   * `postProjectV2Api` 가 현재 경로에서 채운다.
   */
  locale: z.string().optional(),
});

export type ProjectCreateRequestV2Type = z.infer<
  typeof ProjectCreateRequestV2Schema
>;

/**
 * postProjectV2Api 입력 타입. `.default()` 가 걸린 `indexLevel` 만 선택적으로 완화 —
 * 호출부가 실제로 보내는 raw payload 와 일치한다(불필요한 필드 강제 없음). `contents` 등 나머지는
 * 출력 타입 그대로라 normalizeContents 와 호환. (postProjectV2Api 는 parse 하지 않으므로 런타임 영향 없음.)
 */
export type ProjectCreateRequestV2Input = Omit<
  ProjectCreateRequestV2Type,
  'indexLevel'
> & { indexLevel?: number };

// ===================================
// v2.0 Response Schema
// ===================================

export const StatusProgressSchema = z.object({
  current: z.number(),
  total: z.number(),
  label: z.string(),
  /** i18n 번역 키 (예: "project.status.indexing") */
  statusKey: z.string(),
});

export type StatusProgressType = z.infer<typeof StatusProgressSchema>;

export const ContentUploadInfoSchema = z.object({
  contentId: z.number(),
  fileName: z.string(),
  preSignedUrl: z.string(),
  thumbnailPreSignedUrl: z.string().optional(),
});

export type ContentUploadInfoType = z.infer<typeof ContentUploadInfoSchema>;

export const ProjectDtoV2Schema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.nativeEnum(DtoProjectType),
  outputLength: z.nativeEnum(DtoOutputLength),
  aspectRatio: z.nativeEnum(DtoAspectRatio),
  resolution: z.nativeEnum(DtoResolution),
  basePrompt: z.string().nullable(),
  status: z.nativeEnum(DtoProjectStatus),
  createdAt: z.string(),
  updatedAt: z.string(),
  // v1.0 레거시
  creationStepCurrentIndex: z.number().nullable().optional(),
  creationStepTotalCount: z.number().optional(),
  // v2.0 진행률
  statusProgress: StatusProgressSchema.nullable().optional(),
});

export type ProjectDtoV2Type = z.infer<typeof ProjectDtoV2Schema>;

export const ProjectCreateResponseV2Schema = z.object({
  project: ProjectDtoV2Schema,
  uploadInfos: z.array(ContentUploadInfoSchema).nullable().optional(),
});

export type ProjectCreateResponseV2Type = z.infer<
  typeof ProjectCreateResponseV2Schema
>;

type ProjectCreateResponseV2 = ApiResponse<ProjectCreateResponseV2Type>;

// ===================================
// v2.0 API Functions
// ===================================

/**
 * LOCAL_FILE의 VIDEO content에 대해 resolution을 자동 계산
 * width/height가 있으면 getResolutionFromDimensions로 계산, 없으면 0(auto)
 */
const normalizeContents = (
  contents?: ProjectContentInputType[],
): ProjectContentInputType[] | undefined => {
  if (!contents) return contents;

  return contents.map((content) => {
    // LOCAL_FILE 타입이고 VIDEO content인 경우만 처리
    if (
      content.type === 'LOCAL_FILE' &&
      content.content?.type === 'VIDEO' &&
      content.content.width &&
      content.content.height
    ) {
      return {
        ...content,
        content: {
          ...content.content,
          resolution: getResolutionFromDimensions(
            content.content.width,
            content.content.height,
          ),
        },
      };
    }
    return content;
  });
};

/**
 * v2.0 프로젝트 생성 API
 *
 * - 프로젝트 생성 + Content 메타 생성
 * - LocalFile 타입의 경우 presigned URL 발급
 * - status: PREPARE_CONTENTS (업로드 대기)
 * - VIDEO content의 resolution은 width/height 기반으로 자동 계산
 */
export const postProjectV2Api = async (form: ProjectCreateRequestV2Input) => {
  try {
    // contents의 resolution 자동 계산
    const normalizedForm = {
      ...form,
      contents: normalizeContents(form.contents),
      // 호출부가 명시적으로 넘긴 값이 있으면 그대로 존중한다.
      locale: form.locale ?? resolveCurrentLocale(),
    };

    const response = await axiosClient.post<ProjectCreateResponseV2>(
      '/v2.0/project',
      normalizedForm,
    );
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const usePostProjectV2Api = () => {
  return useMutation<
    ProjectCreateResponseV2,
    AxiosError<ErrorResponse>,
    ProjectCreateRequestV2Input
  >({
    mutationFn: postProjectV2Api,
  });
};
